import './Style.scss';
import * as React from 'react';
import { Button, Dropdown, Form, ListGroup, Modal } from 'react-bootstrap';
import { EmailTemplate } from 'Interfaces/EmailTemplateInterface';
import { getEmailTemplates, deleteEmailTemplate, createEmailTemplate } from 'api/emailTemplatesApi';
import { ConfirmationModal } from 'views/Modals/ConfirmationModal';
import { getLocalization } from 'global/global';
import { useAppSelector } from 'index';
import { StateInterface } from 'Interfaces/StateInterface';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

type View = 'menu' | 'insert' | 'manage';

interface Props {
  body: string;
  onInsertTemplate: (templateBody: string) => void;
}

const label = (key: string, fallback: string) => {
  const value = getLocalization(key);
  return value && value !== key ? value : fallback;
};

export default function EmailTemplates({ body, onInsertTemplate }: Props) {

  const dispatch = useDispatch();
  const templates = useAppSelector((state: StateInterface) => state.emailTemplates);
  const clientPersist = useAppSelector((state: StateInterface) => state.clientPersist);

  const [show, setShow] = React.useState(false);
  const [view, setView] = React.useState<View>('menu');
  const [search, setSearch] = React.useState('');
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const [saveName, setSaveName] = React.useState('');
  const [deleteId, setDeleteId] = React.useState<number | null>(null);

  const loadTemplates = React.useCallback(() => {
    void dispatch(getEmailTemplates());
  }, []);

  React.useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  React.useEffect(() => {
    setSearch('');
  }, [view]);

  const handleToggle = (nextShow: boolean) => {
    setShow(nextShow);
    if (nextShow) {
      setView('menu');
      setSearch('');
      loadTemplates();
    }
  };

  const handleInsertClick = (template: EmailTemplate) => {
    onInsertTemplate(template.body);
    setShow(false);
    setView('menu');
  };

  const handleSaveClick = () => {
    if (body === '') {
      toast(getLocalization('noTemplateToSave'), {
        type: toast.TYPE.ERROR
      });
      return;
    }
    setShow(false);
    setSaveName('');
    setShowSaveModal(true);
  };

  const handleSaveConfirm = () => {
    const name = saveName.trim();
    if (!name) {
      return;
    }
    void dispatch(createEmailTemplate({ name, body, userId: Number(`${clientPersist.user_id}`) }));
    setShowSaveModal(false);
    setSaveName('');
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) {
      return;
    }
    void dispatch(deleteEmailTemplate(deleteId));
    setDeleteId(null);
  };

  const filteredTemplates = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return templates;
    }
    return templates.filter(t => t.name.toLowerCase().includes(q));
  }, [templates, search]);

  return (
    <>
      <Dropdown
        show={show}
        onToggle={handleToggle}
        align="end"
        autoClose="outside"
      >
        <Dropdown.Toggle size="sm" variant="outline-secondary" id="email-templates-toggle">
          {getLocalization('templates')}
        </Dropdown.Toggle>
        <Dropdown.Menu className="p-2" style={{ minWidth: 260 }}>
          {view === 'menu' && (
            <>
              <Dropdown.Item onClick={() => setView('insert')}>
                {getLocalization('insertTemplate')}
              </Dropdown.Item>
              <Dropdown.Item onClick={handleSaveClick}>
                {getLocalization('saveAsTemplate')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setView('manage')}>
                {getLocalization('manageTemplates')}
              </Dropdown.Item>
            </>
          )}

          {view === 'insert' && (
            <>
              <div className="d-flex align-items-center mb-2">
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 me-2"
                  onClick={() => setView('menu')}
                >
                  ←
                </Button>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder={getLocalization('searchTemplates')}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              {filteredTemplates.length === 0 ? (
                <div className="text-muted small p-2">
                  {getLocalization('noTemplates')}
                </div>
              ) : (
                <ListGroup variant="flush" className="email-templates-dropdown">
                  {filteredTemplates.map(template => (
                    <ListGroup.Item
                      key={template.id}
                      as="div"
                      action
                      className="px-2 py-2 pointer"
                      onClick={() => handleInsertClick(template)}
                    >
                      {template.name}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </>
          )}
          {view === 'manage' && (
            <>
              <div className="d-flex align-items-center mb-2">
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 me-2"
                  onClick={() => setView('menu')}
                >
                  ←
                </Button>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder={getLocalization('searchTemplates')}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
                {/* <strong className="small">{getLocalization('manageTemplates')}</strong>*/}
              </div>
              {filteredTemplates.length === 0 ? (
                <div className="text-muted small p-2">
                  {getLocalization('noTemplates')}
                </div>
              ) : (
                <ListGroup variant="flush" className="email-templates-dropdown">
                  {filteredTemplates.map(template => (
                    <ListGroup.Item
                      key={template.id}
                      className="px-2 py-1 small d-flex justify-content-between align-items-center"
                    >
                      <span className="text-truncate me-2">{template.name}</span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => setDeleteId(template.id)}
                        aria-label={getLocalization('deleteData')}
                        data-testid={`delete-template-${template.id}`}
                      >
                        <i className="fa fa-trash" aria-hidden="true" />
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>

      <Modal
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6">
            {label('saveAsTemplate', 'Save as template')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="small">
              {label('templateName', 'Template name')}
            </Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              autoFocus
              data-testid="save-template-name-input"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setShowSaveModal(false)}>
            {label('cancel', 'Cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleSaveConfirm}
            disabled={!saveName.trim()}
            data-testid="save-template-confirm-btn"
          >
            {label('save', 'Save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {deleteId && (
        <ConfirmationModal
          onClose={() => setDeleteId(null)}
          onCancel={() => setDeleteId(null)}
          onConfirm={handleDeleteConfirm}
          localizations={{
            header: <span>{getLocalization('confirm')}</span>,
            body: <span>{getLocalization('deleteTemplate')}</span>,
            confirm: getLocalization('deleteData'),
            cancel: getLocalization('cancel'),
            confirmStyle: 'danger',
          }}
        />
      )}
    </>
  );
}
